/**
 * DRE Drill-Down Route
 *
 * Generic drill-down page for any DRE line item.
 * Uses GenericDrillDown component for filtering, grouping, and comparison.
 * Supports granularity (aggregation type) passed from DRE page.
 *
 * Route: /_auth/financial/dre/drilldown/$lineItem
 *
 * @see docs/research/trpc/frontend/src/routes/dre/drilldown.$lineItem.tsx - Reference implementation
 */

import { trpc } from '@/api/trpc';
import type { PeriodDates } from '@/app/financial/dre/components';
import { GenericDrillDown } from '@/app/financial/dre/drill-down/GenericDrillDown';
import type {
  ChartType,
  ComparisonDates,
  DrillDownGranularity,
} from '@/app/financial/dre/drill-down/types';
import { FinancialSkeleton, FinancialEmptyState } from '@/app/financial/shared/components';
import type { DRELineItem } from '@ds/ds-api-types/types';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

// ============================================================================
// CONSTANTS
// ============================================================================

// Line item labels for display
const LINE_ITEM_LABELS: Record<DRELineItem, string> = {
  ReceitaBruta: 'Receita Bruta',
  ReceitaBruta_NFTributavel: 'Receita Bruta - NF Tributavel',
  ReceitaBruta_ReciboEncaminhadora: 'Receita Bruta - Recibo Encaminhadora',
  ReceitaBruta_CDC: 'Receita Bruta - Carta de Credito',
  ReceitaBruta_FluxoGerencial: 'Receita Bruta - Fluxo Gerencial',
  Repasse: 'Repasse / Simples Remessa',
  BV: 'Bonus de Veiculacao (BV)',
  PIS: 'PIS',
  PIS_Recolhido: 'PIS Recolhido',
  PIS_RetidoNF: 'PIS Retido NF',
  PIS_RetidoCR: 'PIS Retido CR',
  COFINS: 'COFINS',
  COFINS_Recolhido: 'COFINS Recolhido',
  COFINS_RetidoNF: 'COFINS Retido NF',
  COFINS_RetidoCR: 'COFINS Retido CR',
  ISS: 'ISS',
  ISS_Recolhido: 'ISS Recolhido',
  ISS_Retido: 'ISS Retido',
  ServicosPrestados: 'Servicos Prestados (Terceiros)',
  Timesheet: 'Folha Timesheet',
  Folha: 'Folha Comum',
  MaoDeObra: 'Mao de Obra',
  Freelancer: 'Freelancer',
  FreelancerFixo: 'Freelancer Fixo',
  FreelancerEmpresa: 'Freelancer Empresa',
  Bonificacao: 'Bonificacao',
  Bonificacao_Fluxo: 'Bonificacao - Fluxo',
  Bonificacao_Financeiro: 'Bonificacao - Financeiro',
  Bonificacao_Compras: 'Bonificacao - Compras',
  DespesasOperacionais: 'Despesas Operacionais',
  ReceitasFinanceiras: 'Receitas Financeiras',
  ReceitasFinanceiras_Banco: 'Receitas Financeiras - Banco',
  ReceitasFinanceiras_Fluxo: 'Receitas Financeiras - Fluxo',
  DespesasFinanceiras: 'Despesas Financeiras',
  DespesasFinanceiras_Banco: 'Despesas Financeiras - Banco',
  DespesasFinanceiras_Fluxo: 'Despesas Financeiras - Fluxo',
  IRPJ: 'IRPJ',
  IRPJ_RetidoNF: 'IRRF Retido NF',
  IRPJ_Recolhido: 'IRPJ Recolhido',
  IRPJ_Fluxo: 'IRPJ Fluxo',
  ContribuicaoSocial: 'Contribuicao Social (CSLL)',
  ContribuicaoSocial_RetidoNF: 'CSLL Retido NF',
  ContribuicaoSocial_RetidoCR: 'CSLL Retido CR',
  ContribuicaoSocial_Recolhido: 'CSLL Recolhido',
  ContribuicaoSocial_Fluxo: 'CSLL Fluxo',
};

// Valid granularity values (order: Acumulado -> Por Ano -> Por Trimestre -> Por Mes)
const VALID_GRANULARITIES: DrillDownGranularity[] = ['accumulated', 'yearly', 'quarterly', 'monthly'];

// Valid chart types
const VALID_CHART_TYPES: ChartType[] = ['bar', 'line'];

// ============================================================================
// SEARCH PARAMS VALIDATION
// ============================================================================

interface DrillDownSearchParams {
  startDate: string;
  endDate: string;
  compareStartDate?: string;
  compareEndDate?: string;
  granularity?: DrillDownGranularity;
  chartType?: ChartType;
  selectedDimensions?: string[];
}

// ============================================================================
// ROUTE DEFINITION
// ============================================================================

export const Route = createFileRoute('/_auth/financial/dre/drilldown/$lineItem')({
  validateSearch: (search: Record<string, unknown>): DrillDownSearchParams => ({
    startDate: (search.startDate as string) || '',
    endDate: (search.endDate as string) || '',
    compareStartDate: (search.compareStartDate as string) || undefined,
    compareEndDate: (search.compareEndDate as string) || undefined,
    granularity: VALID_GRANULARITIES.includes(search.granularity as DrillDownGranularity)
      ? (search.granularity as DrillDownGranularity)
      : undefined,
    chartType: VALID_CHART_TYPES.includes(search.chartType as ChartType)
      ? (search.chartType as ChartType)
      : undefined,
    selectedDimensions: Array.isArray(search.selectedDimensions)
      ? (search.selectedDimensions as string[])
      : typeof search.selectedDimensions === 'string'
        ? [search.selectedDimensions]
        : undefined,
  }),
  component: DrillDownComponent,
});

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Parse date string as local time (avoids UTC timezone shift)
 */
function parseLocalDate(dateStr: string): Date {
  // "2025-09-01" -> "2025-09-01T00:00:00" (local time)
  return new Date(dateStr + 'T00:00:00');
}

/**
 * Format date for display in pt-BR locale
 */
function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = parseLocalDate(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR');
}

// ============================================================================
// LOADING SKELETON (Uses Design System)
// ============================================================================

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="p-8 bg-brand-dark min-h-screen">
      <FinancialSkeleton variant="full-dashboard" />
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function DrillDownComponent(): JSX.Element {
  const { lineItem } = Route.useParams();
  const {
    startDate,
    endDate,
    compareStartDate,
    compareEndDate,
    granularity,
    chartType,
    selectedDimensions,
  } = Route.useSearch();
  const navigate = useNavigate();

  // Granularity state (from URL or default to 'monthly')
  const [currentGranularity, setCurrentGranularity] = useState<DrillDownGranularity>(
    granularity || 'monthly'
  );

  // Chart type state (from URL or default to 'bar')
  const [currentChartType, setCurrentChartType] = useState<ChartType>(
    chartType || 'bar'
  );

  // Selected dimensions for chart aggregation (multi-select)
  const [currentSelectedDimensions, setCurrentSelectedDimensions] = useState<string[]>(
    selectedDimensions || []
  );

  // Comparison state from preset selector
  const [comparisonDates, setComparisonDates] = useState<ComparisonDates | null>(
    compareStartDate && compareEndDate
      ? { startDate, endDate, compareStartDate, compareEndDate }
      : null
  );

  // Effective dates (use comparison dates if set, otherwise use URL dates)
  const effectiveStartDate = comparisonDates?.startDate || startDate;
  const effectiveEndDate = comparisonDates?.endDate || endDate;
  const effectiveCompStartDate = comparisonDates?.compareStartDate || '';
  const effectiveCompEndDate = comparisonDates?.compareEndDate || '';

  // Validate line item is a known DRE line item
  const isValidLineItem = lineItem in LINE_ITEM_LABELS;
  const typedLineItem = lineItem as DRELineItem;

  // Fetch main period data - use fallback dates if empty to avoid invalid Date errors
  const {
    data: drillDownData,
    isLoading,
    error,
    refetch,
  } = trpc.dre.getLineItemDetail.useQuery(
    {
      lineItem: typedLineItem,
      dataInicial: effectiveStartDate ? parseLocalDate(effectiveStartDate) : new Date(),
      dataFinal: effectiveEndDate ? parseLocalDate(effectiveEndDate) : new Date(),
    },
    {
      enabled: isValidLineItem && !!effectiveStartDate && !!effectiveEndDate,
      retry: false,
    }
  );

  // Fetch comparison period data (if enabled)
  const {
    data: comparisonData,
  } = trpc.dre.getLineItemDetail.useQuery(
    {
      lineItem: typedLineItem,
      dataInicial: effectiveCompStartDate ? parseLocalDate(effectiveCompStartDate) : new Date(),
      dataFinal: effectiveCompEndDate ? parseLocalDate(effectiveCompEndDate) : new Date(),
    },
    {
      enabled: !!effectiveCompStartDate && !!effectiveCompEndDate && isValidLineItem,
      retry: false,
    }
  );

  const label = LINE_ITEM_LABELS[typedLineItem] || lineItem;
  const subtitle = `Periodo: ${formatDate(effectiveStartDate)} a ${formatDate(effectiveEndDate)}`;

  // Handle back navigation - preserve granularity
  const handleBack = useCallback(() => {
    void navigate({
      to: '/financial/dre',
      search: { startDate, endDate, granularity: currentGranularity } as Record<string, string>,
    });
  }, [navigate, startDate, endDate, currentGranularity]);

  // Handle comparison change from preset selector
  const handleComparisonChange = useCallback((dates: ComparisonDates | null) => {
    setComparisonDates(dates);
    // Update URL with new comparison dates
    void navigate({
      to: '/financial/dre/drilldown/$lineItem',
      params: { lineItem },
      search: {
        startDate: dates?.startDate || startDate,
        endDate: dates?.endDate || endDate,
        compareStartDate: dates?.compareStartDate || undefined,
        compareEndDate: dates?.compareEndDate || undefined,
        granularity: currentGranularity,
      },
    });
  }, [lineItem, startDate, endDate, currentGranularity, navigate]);

  // Handle period preset change (Ano Atual, Ultimos 12/6/3 meses)
  const handlePeriodChange = useCallback((dates: PeriodDates) => {
    // Clear comparison when changing period
    setComparisonDates(null);
    // Update URL with new dates
    void navigate({
      to: '/financial/dre/drilldown/$lineItem',
      params: { lineItem },
      search: {
        startDate: dates.startDate,
        endDate: dates.endDate,
        granularity: currentGranularity,
      },
    });
  }, [lineItem, currentGranularity, navigate]);

  // Handle granularity change
  const handleGranularityChange = useCallback((newGranularity: DrillDownGranularity) => {
    setCurrentGranularity(newGranularity);
    // Update URL with new granularity
    void navigate({
      to: '/financial/dre/drilldown/$lineItem',
      params: { lineItem },
      search: {
        startDate,
        endDate,
        compareStartDate: comparisonDates?.compareStartDate || undefined,
        compareEndDate: comparisonDates?.compareEndDate || undefined,
        granularity: newGranularity,
        chartType: currentChartType,
        selectedDimensions: currentSelectedDimensions.length > 0 ? currentSelectedDimensions : undefined,
      },
    });
  }, [lineItem, startDate, endDate, comparisonDates, currentChartType, currentSelectedDimensions, navigate]);

  // Handle chart type change
  const handleChartTypeChange = useCallback((newChartType: ChartType) => {
    setCurrentChartType(newChartType);
    // Update URL with new chart type
    void navigate({
      to: '/financial/dre/drilldown/$lineItem',
      params: { lineItem },
      search: {
        startDate,
        endDate,
        compareStartDate: comparisonDates?.compareStartDate || undefined,
        compareEndDate: comparisonDates?.compareEndDate || undefined,
        granularity: currentGranularity,
        chartType: newChartType,
        selectedDimensions: currentSelectedDimensions.length > 0 ? currentSelectedDimensions : undefined,
      },
    });
  }, [lineItem, startDate, endDate, comparisonDates, currentGranularity, currentSelectedDimensions, navigate]);

  // Handle dimension selection toggle (multi-select)
  const handleDimensionToggle = useCallback((dimensionKey: string) => {
    const newDimensions = currentSelectedDimensions.includes(dimensionKey)
      ? currentSelectedDimensions.filter(d => d !== dimensionKey)
      : [...currentSelectedDimensions, dimensionKey];

    setCurrentSelectedDimensions(newDimensions);
    // Update URL with new dimensions
    void navigate({
      to: '/financial/dre/drilldown/$lineItem',
      params: { lineItem },
      search: {
        startDate,
        endDate,
        compareStartDate: comparisonDates?.compareStartDate || undefined,
        compareEndDate: comparisonDates?.compareEndDate || undefined,
        granularity: currentGranularity,
        chartType: currentChartType,
        selectedDimensions: newDimensions.length > 0 ? newDimensions : undefined,
      },
    });
  }, [lineItem, startDate, endDate, comparisonDates, currentGranularity, currentChartType, currentSelectedDimensions, navigate]);

  // Clear all selected dimensions
  const handleClearDimensions = useCallback(() => {
    setCurrentSelectedDimensions([]);
    void navigate({
      to: '/financial/dre/drilldown/$lineItem',
      params: { lineItem },
      search: {
        startDate,
        endDate,
        compareStartDate: comparisonDates?.compareStartDate || undefined,
        compareEndDate: comparisonDates?.compareEndDate || undefined,
        granularity: currentGranularity,
        chartType: currentChartType,
      },
    });
  }, [lineItem, startDate, endDate, comparisonDates, currentGranularity, currentChartType, navigate]);

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="p-8 bg-brand-dark min-h-screen">
        <FinancialEmptyState
          variant="error"
          title="Erro ao carregar dados"
          description={error.message}
          actionLabel="Tentar Novamente"
          onAction={() => { void refetch(); }}
          secondaryActionLabel="Voltar ao DRE"
          onSecondaryAction={handleBack}
        />
      </div>
    );
  }

  // Missing date params state - show error if dates are invalid/missing
  if (!effectiveStartDate || !effectiveEndDate) {
    return (
      <div className="p-8 bg-brand-dark min-h-screen">
        <FinancialEmptyState
          variant="error"
          title="Datas inválidas"
          description="Por favor, volte ao DRE e selecione um período válido."
          actionLabel="Voltar ao DRE"
          onAction={handleBack}
        />
      </div>
    );
  }

  // No data state
  if (!drillDownData || !drillDownData.rows) {
    return (
      <div className="p-8 bg-brand-dark min-h-screen">
        <h2 className="text-2xl font-display font-bold text-white mb-6">{label}</h2>
        <FinancialEmptyState
          variant="no-data"
          title="Sem dados disponíveis"
          description="Nenhum dado encontrado para o período selecionado."
          actionLabel="Voltar ao DRE"
          onAction={handleBack}
        />
      </div>
    );
  }

  // Transform data for GenericDrillDown
  const rows = drillDownData.rows.map(row => ({
    ...row,
    // Ensure Data is a string for the component
    Data: row.Data ? String(row.Data) : null,
  }));

  const comparisonRows = comparisonData?.rows?.map(row => ({
    ...row,
    Data: row.Data ? String(row.Data) : null,
  })) || null;

  return (
    <GenericDrillDown
      data={rows}
      title={label}
      subtitle={subtitle}
      comparisonData={comparisonRows}
      onBack={handleBack}
      backLabel="Voltar ao DRE"
      onComparisonChange={handleComparisonChange}
      onPeriodChange={handlePeriodChange}
      startDate={parseLocalDate(effectiveStartDate)}
      endDate={parseLocalDate(effectiveEndDate)}
      comparisonStartDate={effectiveCompStartDate ? parseLocalDate(effectiveCompStartDate) : undefined}
      comparisonEndDate={effectiveCompEndDate ? parseLocalDate(effectiveCompEndDate) : undefined}
      granularity={currentGranularity}
      onGranularityChange={handleGranularityChange}
      chartType={currentChartType}
      onChartTypeChange={handleChartTypeChange}
      selectedDimensions={currentSelectedDimensions}
      onDimensionToggle={handleDimensionToggle}
      onClearDimensions={handleClearDimensions}
    />
  );
}

export default DrillDownComponent;
